import { useState, type JSX } from "react";
import type {
  CaseSelection,
  Difficulty,
  TaxonomySectorDto,
  TaxonomyTopicDto,
} from "../api/types";
import { messages } from "../messages";

interface SelectionPageProps {
  taxonomy: readonly TaxonomySectorDto[];
  isBusy: boolean;
  error: string | null;
  isAdmin?: boolean;
  onStart: (selection: CaseSelection) => void;
  onLogout: () => void;
  onOpenAdmin?: () => void;
}

const difficulties: readonly Difficulty[] = ["Easy", "Medium", "Hard"];

function getDifficultyLabel(difficulty: Difficulty): string {
  switch (difficulty) {
    case "Easy":
      return messages.easy;
    case "Medium":
      return messages.medium;
    case "Hard":
      return messages.hard;
    default: {
      const exhaustiveCheck: never = difficulty;
      return exhaustiveCheck;
    }
  }
}

/** Guides players through taxonomy-backed case selection. */
export function SelectionPage({
  taxonomy,
  isBusy,
  error,
  isAdmin = false,
  onStart,
  onLogout,
  onOpenAdmin,
}: SelectionPageProps): JSX.Element {
  const [selectedSector, setSelectedSector] = useState<TaxonomySectorDto | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<TaxonomyTopicDto | null>(null);

  const chooseSector = (sector: TaxonomySectorDto): void => {
    setSelectedSector(sector);
    setSelectedTopic(null);
  };

  return (
    <main className="page selection-page">
      <header className="toolbar">
        <div>
          <p className="eyebrow">Nytt case</p>
          <h1>Velg fagområde</h1>
        </div>
        <div className="choices">
          {isAdmin && onOpenAdmin !== undefined && (
            <button type="button" className="secondary" onClick={onOpenAdmin}>
              Faglig kontroll
            </button>
          )}
          <button type="button" className="secondary" onClick={onLogout}>{messages.logout}</button>
        </div>
      </header>

      {error !== null && <p role="alert">{error}</p>}
      {taxonomy.length === 0 && !isBusy && <p>Ingen aktive caser er tilgjengelige ennå.</p>}

      <section aria-labelledby="sector-heading">
        <h2 id="sector-heading">1. Fagområde</h2>
        <div className="choice-grid">
          {taxonomy.map((sector) => (
            <button
              type="button"
              className={selectedSector?.slug === sector.slug ? "selected" : "choice-card"}
              disabled={isBusy}
              key={sector.slug}
              onClick={() => { chooseSector(sector); }}
            >
              {sector.name}
            </button>
          ))}
        </div>
      </section>

      {selectedSector !== null && (
        <section aria-labelledby="topic-heading">
          <h2 id="topic-heading">2. Tema</h2>
          <div className="choice-grid">
            {selectedSector.topics.map((topic) => {
              const total = Object.values(topic.activeCaseCounts)
                .reduce((sum, count) => sum + count, 0);
              return (
                <button
                  type="button"
                  className={selectedTopic?.slug === topic.slug ? "selected" : "choice-card"}
                  disabled={isBusy || total === 0}
                  key={topic.slug}
                  onClick={() => { setSelectedTopic(topic); }}
                >
                  {topic.name} ({total} caser)
                </button>
              );
            })}
          </div>
        </section>
      )}

      {selectedSector !== null && selectedTopic !== null && (
        <section aria-labelledby="difficulty-heading">
          <h2 id="difficulty-heading">3. Vanskelighetsgrad</h2>
          <div className="choice-grid">
            {difficulties.map((difficulty) => {
              const count = selectedTopic.activeCaseCounts[difficulty];
              return (
                <button
                  type="button"
                  className="choice-card"
                  disabled={isBusy || count === 0}
                  key={difficulty}
                  onClick={() => {
                    onStart({
                      sector: selectedSector.slug,
                      topic: selectedTopic.slug,
                      difficulty,
                    });
                  }}
                >
                  {getDifficultyLabel(difficulty)} ({count})
                </button>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
}
